// components/FloatingMenu.js
import React, { useState, useEffect, useMemo } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { authService } from '../services/authService';
import { db } from '../config/firebase';
import { Icons } from '../utils/icons';
import { menuItems } from '../utils/constants';
import { useAuth } from '../contexts/AuthContext';
import { construirArbolMenu } from '../utils/menuAdmin';

const MENU_HEADER = {
  admin:                   { emoji: '🏢', label: 'Dirección' },
  coordinador_operaciones: { emoji: '⚙️', label: 'Operaciones' },
  atencion_cliente:        { emoji: '📞', label: 'Atención al Cliente' },
};

const FloatingMenu = ({ isOpen, onToggle, onSelectView, userRole }) => {
  const { user, updateUser } = useAuth();
  const [expandedItem, setExpandedItem] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const [editandoOrden, setEditandoOrden] = useState(false);
  const [menuOverrides, setMenuOverrides] = useState(null); // visibilidad/agrupación editadas por el admin

  // Sobrescrituras del Editor de menú (solo admin) — visibilidad por rol y
  // reagrupación. En tiempo real, así un cambio del admin se ve al instante.
  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, 'configuracion', 'menuAdmin'),
      (snap) => setMenuOverrides(snap.exists() ? snap.data() : null),
      () => setMenuOverrides(null)
    );
    return () => unsub();
  }, []);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // Bloqueo de scroll en móvil cuando el menú está abierto
  useEffect(() => {
    if (isMobile && isOpen) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [isOpen, isMobile]);

  // Cerrar submenús cuando se cierra el menú principal
  useEffect(() => {
    if (!isOpen) setExpandedItem(null);
  }, [isOpen]);

  // Salir del modo de edición de orden cuando se cierra el menú
  useEffect(() => {
    if (!isOpen) setEditandoOrden(false);
  }, [isOpen]);

  // Items visibles (por rol), reordenados según la preferencia guardada del
  // usuario (user.menuOrder = array de ids). Los items sin preferencia
  // guardada (nuevos, o si nunca se ha reordenado) van al final en su orden
  // original de menuItems.
  const itemsOrdenados = useMemo(() => {
    const visibles = construirArbolMenu(menuItems, menuOverrides, userRole);
    const orden = user?.menuOrder || [];
    if (orden.length === 0) return visibles;
    const porId = new Map(visibles.map(i => [i.id, i]));
    const ordenados = orden.map(id => porId.get(id)).filter(Boolean);
    const faltantes = visibles.filter(i => !orden.includes(i.id));
    return [...ordenados, ...faltantes];
  }, [userRole, user?.menuOrder, menuOverrides]);

  const moverItem = async (id, direccion) => {
    const ids = itemsOrdenados.map(i => i.id);
    const idx = ids.indexOf(id);
    const nuevoIdx = idx + direccion;
    if (nuevoIdx < 0 || nuevoIdx >= ids.length) return;
    const nuevoOrden = [...ids];
    [nuevoOrden[idx], nuevoOrden[nuevoIdx]] = [nuevoOrden[nuevoIdx], nuevoOrden[idx]];
    updateUser({ menuOrder: nuevoOrden }); // aplica al instante, sin esperar red
    try {
      const resp = await fetch('/.netlify/functions/updateMenuOrder', {
        method: 'POST', headers: { 'Content-Type': 'application/json', ...authService.authHeader() },
        body: JSON.stringify({ email: user.email, menuOrder: nuevoOrden }),
      });
      const data = await resp.json();
      if (data.success) updateUser({ menuOrder: data.user.menuOrder });
    } catch { /* el orden ya se aplicó localmente; se reintentará en el próximo movimiento */ }
  };

  const handleItemClick = (item) => {
    if (editandoOrden) return;
    if (item.subitems?.length > 0) {
      setExpandedItem(prev => prev === item.id ? null : item.id);
    } else {
      onSelectView(item.id);
      onToggle();
    }
  };

  const handleSubitemClick = (subitemId) => {
    onSelectView(subitemId);
    onToggle();
  };

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 md:bg-black/20 transition-opacity duration-300"
          onClick={onToggle}
          aria-hidden="true"
        />
      )}

      {/* Botón flotante */}
      <button
        onClick={onToggle}
        className={`
          fixed z-50 top-6 left-6 w-16 h-16 rounded-full shadow-xl transition-all duration-300
          flex items-center justify-center text-white
          bg-gradient-to-br from-red-600 to-red-700
          hover:from-red-700 hover:to-red-800 hover:shadow-2xl
          active:scale-95
          ${isOpen ? 'rotate-90 scale-110' : 'hover:scale-105'}
        `}
        aria-label={isOpen ? 'Cerrar menú' : 'Abrir menú'}
      >
        {isOpen
          ? <Icons.X className="w-7 h-7" />
          : <Icons.Menu className="w-7 h-7" />
        }
      </button>

      {/* Panel del menú */}
      <div
        className={`
          fixed z-50 bg-white shadow-2xl border border-gray-100 rounded-2xl
          top-20 left-4 sm:left-6 w-[calc(100vw-32px)] sm:w-72
          max-h-[calc(100vh-100px)] overflow-y-auto
          transition-all duration-300 ease-out
          ${isOpen ? 'opacity-100 scale-100 translate-y-0 visible pointer-events-auto' : 'opacity-0 scale-95 -translate-y-4 invisible pointer-events-none'}
        `}
      >
        {/* Header dinámico por rol */}
        <div className="border-b border-gray-100 bg-gradient-to-r from-gray-50 to-gray-100 p-5 rounded-t-2xl flex items-center justify-between gap-2">
          <h3 className="font-bold text-gray-900 text-xl">
            {MENU_HEADER[userRole]?.emoji ?? '🚀'}{' '}
            {MENU_HEADER[userRole]?.label ?? 'Panel de Control'}
          </h3>
          <button
            onClick={() => { setEditandoOrden(e => !e); setExpandedItem(null); }}
            title={editandoOrden ? 'Terminar de editar el orden' : 'Editar el orden del menú'}
            className={`flex-shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-bold transition-colors ${
              editandoOrden ? 'bg-red-600 text-white' : 'bg-white text-gray-500 border border-gray-200 hover:bg-gray-50'}`}
          >
            {editandoOrden ? <Icons.Check className="w-3.5 h-3.5" /> : <Icons.Edit className="w-3.5 h-3.5" />}
            {editandoOrden ? 'Listo' : 'Orden'}
          </button>
        </div>

        {/* Items filtrados por rol */}
        <div className="p-3 space-y-1">
          {itemsOrdenados.map((item, index) => {
            // construirArbolMenu ya resolvió qué subitems son visibles para este rol
            const visibleSubs = (item.subitems || []).filter(s => s?.icon);
            const hasSubitems = visibleSubs.length > 0;
            // Si el item tiene subitems definidos pero ninguno es visible, ocultarlo
            if (item.subitems?.length > 0 && visibleSubs.length === 0) return null;

            const isExpanded = expandedItem === item.id;
            const IconComponent = Icons[item.icon];
            if (!IconComponent) return null;

            return (
              <div key={item.id}>
                {/* Item principal */}
                <button
                  onClick={() => handleItemClick(item)}
                  className={`
                    w-full text-left rounded-xl flex items-center gap-3
                    transition-all duration-200 px-3 py-3
                    ${editandoOrden ? 'cursor-default' : ''}
                    ${isExpanded
                      ? 'bg-red-50 border border-red-100'
                      : 'hover:bg-gray-50 border border-transparent hover:border-gray-100'}
                  `}
                >
                  <div className={`rounded-lg flex-shrink-0 p-2 transition-colors ${isExpanded ? 'bg-red-100' : 'bg-gray-100'}`}>
                    <IconComponent className={`w-5 h-5 transition-colors ${isExpanded ? 'text-red-600' : 'text-gray-600'}`} />
                  </div>

                  <div className="flex-1 min-w-0 text-left">
                    <p className={`font-semibold text-sm leading-tight ${isExpanded ? 'text-red-700' : 'text-gray-800'}`}>
                      {item.label}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5 leading-snug truncate">
                      {item.description}
                    </p>
                  </div>

                  {editandoOrden ? (
                    <div className="flex flex-col gap-0.5 flex-shrink-0">
                      <span role="button" tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); moverItem(item.id, -1); }}
                        className={`p-1 rounded-md ${index === 0 ? 'text-gray-200' : 'text-gray-500 hover:bg-gray-200'}`}>
                        <Icons.ChevronDown className="w-3.5 h-3.5 rotate-180" />
                      </span>
                      <span role="button" tabIndex={0}
                        onClick={(e) => { e.stopPropagation(); moverItem(item.id, 1); }}
                        className={`p-1 rounded-md ${index === itemsOrdenados.length - 1 ? 'text-gray-200' : 'text-gray-500 hover:bg-gray-200'}`}>
                        <Icons.ChevronDown className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  ) : hasSubitems && (
                    <Icons.ChevronDown
                      className={`w-4 h-4 flex-shrink-0 transition-transform duration-200 ${isExpanded ? 'rotate-180 text-red-500' : 'text-gray-400'}`}
                    />
                  )}
                </button>

                {/* Submenú acordeón — siempre inline, nunca flyout */}
                {hasSubitems && isExpanded && (
                  <div className="ml-4 mt-1 mb-1 border-l-2 border-red-100 pl-3 space-y-1">
                    {visibleSubs.map((subitem, idx) => {
                      const SubIcon = Icons[subitem.icon];
                      if (!SubIcon) return null;

                      return (
                        <button
                          key={subitem.id || idx}
                          onClick={() => handleSubitemClick(subitem.id)}
                          className="w-full text-left rounded-lg flex items-center gap-3 px-3 py-2.5 hover:bg-red-50 transition-all group"
                        >
                          <div className="rounded-md bg-gray-100 group-hover:bg-red-100 transition-colors flex-shrink-0 p-1.5">
                            <SubIcon className="w-4 h-4 text-gray-600 group-hover:text-red-600 transition-colors" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-gray-700 group-hover:text-red-700 transition-colors">
                              {subitem.label}
                            </p>
                            {subitem.description && (
                              <p className="text-xs text-gray-400 truncate mt-0.5">{subitem.description}</p>
                            )}
                          </div>
                          <Icons.ChevronRight className="w-3.5 h-3.5 text-gray-300 group-hover:text-red-400 flex-shrink-0" />
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        <div className={`border-t border-gray-100 bg-gray-50 text-gray-500 text-xs ${isMobile ? 'p-4' : 'p-4 rounded-b-2xl'}`}>
          <div className="flex items-center justify-between mb-1.5">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-green-400 inline-block" />
              Conectado en tiempo real
            </span>
            <span className="text-gray-400">v2.0</span>
          </div>
          <div className="flex items-center gap-2 text-gray-400">
            <kbd className="bg-white border border-gray-200 px-1.5 py-0.5 rounded font-mono text-[10px]">Ctrl+K</kbd>
            <span>Búsqueda global</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default FloatingMenu;
